import React, { useState, useEffect } from 'react';
import UiContent from "../../../Components/Common/UiContent";
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import ResearchDisclaimer from '../../../Components/Common/ResearchDisclaimer';
import { 
    Card, 
    CardBody, 
    Col, 
    Container, 
    Row,
    Progress,
    Table,
    Badge
} from 'reactstrap';
import CountUp from "react-countup";

const PredictionsDashboard = () => {
    document.title = "Predictions Dashboard | AI Financial Prediction Tracker";

    const [stats, setStats] = useState({
        totalPredictions: 0,
        activePredictions: 0,
        completedPredictions: 0,
        averageAccuracy: 0,
        topModels: [],
        recentPredictions: []
    });

    // Mock data (will be replaced with API calls)
    const mockStats = {
        totalPredictions: 147,
        activePredictions: 23,
        completedPredictions: 124,
        averageAccuracy: 72.5,
        topModels: [
            { name: 'GPT-4', predictions: 45, accuracy: 78.2, trend: 'up' },
            { name: 'Claude-3', predictions: 38, accuracy: 75.8, trend: 'up' },
            { name: 'John Trader', predictions: 25, accuracy: 73.1, trend: 'down' },
            { name: 'Gemini-Pro', predictions: 22, accuracy: 69.4, trend: 'up' },
            { name: 'Technical Analysis Bot', predictions: 17, accuracy: 67.8, trend: 'stable' }
        ],
        recentPredictions: [
            { symbol: 'BTC', model: 'GPT-4', status: 'active', confidence: 85, created: '2 hours ago' },
            { symbol: 'ETH', model: 'Claude-3', status: 'completed', confidence: 78, created: '5 hours ago' },
            { symbol: 'AAPL', model: 'John Trader', status: 'active', confidence: 82, created: '1 day ago' },
            { symbol: 'TSLA', model: 'Gemini-Pro', status: 'failed', confidence: 65, created: '2 days ago' }
        ],
        assetBreakdown: [
            { symbol: 'BTC', predictions: 52, accuracy: 74.3 },
            { symbol: 'ETH', predictions: 38, accuracy: 71.8 },
            { symbol: 'AAPL', predictions: 25, accuracy: 69.2 },
            { symbol: 'TSLA', predictions: 18, accuracy: 75.6 },
            { symbol: 'NVDA', predictions: 14, accuracy: 77.1 }
        ]
    };

    useEffect(() => {
        // Load dashboard data (mock for now)
        setStats(mockStats);
    }, []);

    const getStatusBadge = (status) => {
        const colors = {
            'active': 'primary',
            'completed': 'success',
            'failed': 'danger'
        };
        return <Badge color={colors[status]}>{status}</Badge>;
    };

    const getTrendIcon = (trend) => {
        switch (trend) {
            case 'up':
                return <i className="ri-arrow-up-line text-success"></i>;
            case 'down':
                return <i className="ri-arrow-down-line text-danger"></i>;
            case 'stable':
                return <i className="ri-subtract-line text-muted"></i>;
            default:
                return null;
        }
    };

    const StatCard = ({ title, value, subtitle, color = "primary", icon, prefix = "", suffix = "" }) => (
        <Card className="card-animate">
            <CardBody>
                <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0">{title}</p>
                        <div className="d-flex align-items-center">
                            <h4 className={`fs-22 fw-semibold ff-secondary mb-0 text-${color}`}>
                                {prefix}<CountUp start={0} end={value} duration={2} />{suffix}
                            </h4>
                        </div>
                        {subtitle && <p className="text-muted mb-0">{subtitle}</p>}
                    </div>
                    <div className="flex-shrink-0">
                        <div className={`avatar-sm rounded-circle bg-${color}-subtle`}>
                            <span className={`avatar-title rounded-circle fs-16 text-${color}`}>
                                <i className={icon}></i>
                            </span>
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    return (
        <React.Fragment>
            <UiContent />
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Predictions Dashboard" pageTitle="Predictions" />
                    <ResearchDisclaimer className="mb-3" />
                    
                    {/* Stats Cards */}
                    <Row>
                        <Col xl={3} md={6}>
                            <StatCard
                                title="Total Predictions"
                                value={stats.totalPredictions}
                                icon="ri-bar-chart-box-line"
                                color="primary"
                            />
                        </Col>
                        <Col xl={3} md={6}>
                            <StatCard
                                title="Active Predictions"
                                value={stats.activePredictions}
                                icon="ri-pulse-line"
                                color="info"
                            />
                        </Col>
                        <Col xl={3} md={6}>
                            <StatCard
                                title="Completed"
                                value={stats.completedPredictions}
                                icon="ri-checkbox-circle-line"
                                color="success"
                            />
                        </Col>
                        <Col xl={3} md={6}>
                            <StatCard
                                title="Average Accuracy"
                                value={stats.averageAccuracy}
                                suffix="%"
                                icon="ri-target-line"
                                color="warning"
                            />
                        </Col>
                    </Row>

                    <Row>
                        {/* Top Performing Models */}
                        <Col xl={8}>
                            <Card>
                                <div className="card-header align-items-center d-flex">
                                    <h4 className="card-title mb-0 flex-grow-1">Top Performing Models</h4>
                                    <div className="flex-shrink-0">
                                        <button type="button" className="btn btn-soft-info btn-sm">
                                            <i className="ri-file-list-3-line align-middle"></i> View All
                                        </button>
                                    </div>
                                </div>
                                <CardBody>
                                    <div className="table-responsive">
                                        <Table className="table-borderless table-nowrap align-middle mb-0">
                                            <thead className="table-light text-muted">
                                                <tr>
                                                    <th scope="col">Model Name</th>
                                                    <th scope="col">Predictions</th>
                                                    <th scope="col">Accuracy</th>
                                                    <th scope="col">Trend</th>
                                                    <th scope="col">Performance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.topModels.map((model, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar-xs me-2">
                                                                    <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                                                        {model.name.charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <h6 className="fs-14 mb-0">{model.name}</h6>
                                                            </div>
                                                        </td>
                                                        <td>{model.predictions}</td>
                                                        <td>
                                                            <span className={`fw-medium ${model.accuracy >= 75 ? 'text-success' : model.accuracy >= 65 ? 'text-warning' : 'text-danger'}`}>
                                                                {model.accuracy}%
                                                            </span>
                                                        </td>
                                                        <td>{getTrendIcon(model.trend)}</td>
                                                        <td>
                                                            <Progress
                                                                value={model.accuracy}
                                                                color={model.accuracy >= 75 ? 'success' : model.accuracy >= 65 ? 'warning' : 'danger'}
                                                                style={{ height: '6px' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Recent Activity */}
                        <Col xl={4}>
                            <Card>
                                <div className="card-header align-items-center d-flex">
                                    <h4 className="card-title mb-0 flex-grow-1">Recent Predictions</h4>
                                </div>
                                <CardBody>
                                    <div className="vstack gap-3">
                                        {stats.recentPredictions.map((prediction, index) => (
                                            <div key={index} className="d-flex align-items-center">
                                                <div className="avatar-xs me-3">
                                                    <span className="avatar-title rounded-circle bg-soft-info text-info">
                                                        {prediction.symbol.substring(0, 2)}
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1 fs-14">{prediction.symbol} - {prediction.model}</h6>
                                                    <p className="mb-0 fs-12 text-muted">
                                                        {getStatusBadge(prediction.status)} • {prediction.confidence}% confidence
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <small className="text-muted">{prediction.created}</small>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Asset Performance Breakdown */}
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <div className="card-header">
                                    <h4 className="card-title mb-0">Asset Performance Breakdown</h4>
                                </div>
                                <CardBody>
                                    <Row>
                                        {mockStats.assetBreakdown.map((asset, index) => (
                                            <Col lg={2} md={4} sm={6} key={index}>
                                                <div className="text-center border rounded p-3 mb-3">
                                                    <div className="avatar-sm mx-auto mb-3">
                                                        <span className="avatar-title rounded-circle bg-soft-primary text-primary fs-16">
                                                            {asset.symbol}
                                                        </span>
                                                    </div>
                                                    <h5 className="fs-16 mb-1">{asset.predictions}</h5>
                                                    <p className="text-muted mb-2">Predictions</p>
                                                    <div className={`fs-14 fw-medium ${asset.accuracy >= 75 ? 'text-success' : asset.accuracy >= 65 ? 'text-warning' : 'text-danger'}`}>
                                                        {asset.accuracy}% Accuracy
                                                    </div>
                                                    <Progress
                                                        value={asset.accuracy}
                                                        color={asset.accuracy >= 75 ? 'success' : asset.accuracy >= 65 ? 'warning' : 'danger'}
                                                        className="mt-2"
                                                        style={{ height: '4px' }}
                                                    />
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default PredictionsDashboard;
